// src/pages/Sensor/AllSensors.tsx

import {
  AddTwoTone,
  DownloadTwoTone,
  InfoTwoTone,
  LocalShippingTwoTone,
  MemoryTwoTone,
  MoreVertTwoTone,
  PollTwoTone,
  SettingsRemoteTwoTone,
} from '@mui/icons-material'
import { Alert, Avatar, Divider, IconButton } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FolderPool } from '@/types/domain/folder'
import { SensorPool } from '@/types/domain/sensor'

import { getStorage, setStorage } from '../../api'
import {
  AM,
  AMItem,
  DlgSensorsExport,
  DlgSensorPoolInfo,
  KPage,
  TblSensors,
  usePreloader,
} from '../../components'
import {
  OperatorData,
  useFolderPool,
  useOperator,
  useSensorPool,
} from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

type Props = {
  PAGE_CONFIG?: string
}

export const AllSensors = ({ PAGE_CONFIG = 'settings_sensors_all' }: Props) => {
  const rc = useRenderCount()
  console.log('AllSensors', rc)

  const navigate = useNavigate()

  const opData: OperatorData = useOperator()
  const fPool: FolderPool = useFolderPool()
  const sPool: SensorPool = useSensorPool()

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  const exportDialog = config?.exportDialog ?? false
  const maxResults = config?.maxResults ?? 10
  const showStats = config?.showStats ?? false
  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  const [actionMenu, setActionMenu] = useState(null)
  const [dataPool, setDataPool] = useState([])

  const [tblSnapshot, setTblSnapshot] = useState({
    rows: [],
    page: [],
    pageIndex: 0,
    pageSize: maxResults,
    pageCount: 0,
  })

  const data = useMemo(
    () => dataPool.map(s => ({ ...s.attributes, id: s.id })),
    [dataPool]
  )

  useEffect(() => {
    if (fPool.length === 0 || sPool.length === 0) return

    const buildDataPool = () => {
      const data = sPool.map(s => {
        const id = s.attributes.folder_id

        const folder_name =
          fPool.find(f => Number(f.id) === id)?.attributes?.name ?? '-'

        return { ...s, attributes: { ...s.attributes, folder_name } }
      })
      setDataPool(data)
    }

    buildDataPool()
  }, [fPool, sPool])

  const gotoSensorPage = useCallback(
    id => navigate(`/sensors/${id}`),
    [navigate]
  )

  const onAddSensor = () => navigate('/sensors/new')
  const onCloseAM = () => setActionMenu(null)
  const onOpenAM = event => setActionMenu(event.currentTarget)

  const onCloseExport = () =>
    setConfig(prev => ({ ...prev, exportDialog: false }))
  const onCloseStats = () => setConfig(prev => ({ ...prev, showStats: false }))
  const onExport = () => setConfig(prev => ({ ...prev, exportDialog: true }))
  const onOpenStats = () => setConfig(prev => ({ ...prev, showStats: true }))

  const onGotoVirtualSensors = () => navigate('/virtualsensors')
  const onGotoDeviations = () => navigate('/sensors/calculon')
  const onGotoPayloads = () => navigate('/sensors/payloads')

  const { isLoading } = usePreloader()

  const amData = [
    { caption: 'Neu', icon: <AddTwoTone />, fn: onAddSensor },
    { caption: 'Info', icon: <InfoTwoTone />, fn: onOpenStats },
    { separator: <Divider />, key: 0 },
    { caption: 'Exportieren', icon: <DownloadTwoTone />, fn: onExport },
    {
      caption: 'Virtuelle Sensoren',
      icon: <SettingsRemoteTwoTone />,
      fn: onGotoVirtualSensors,
    },
    { caption: 'Abweichungen', icon: <PollTwoTone />, fn: onGotoDeviations },
    { caption: 'Payloads', icon: <LocalShippingTwoTone />, fn: onGotoPayloads },
  ]

  return opData?.isAdmin ? (
    <KPage
      action={
        <IconButton onClick={onOpenAM}>
          <MoreVertTwoTone />
        </IconButton>
      }
      avatar={
        <Avatar>
          <MemoryTwoTone />
        </Avatar>
      }
      isLoading={isLoading}
      title={`${maxResults}/${tblSnapshot.rows.length} Sensoren`}
    >
      <AM
        anchorEl={actionMenu}
        caption={'Sensoren'}
        onClose={onCloseAM}
        open={Boolean(actionMenu)}
        historyUrlTarget={'dashboard'}
      >
        {amData.map(item =>
          !item.separator ? (
            <AMItem
              caption={item.caption}
              icon={item.icon}
              key={item.caption}
              onClick={item.fn}
            />
          ) : (
            item.key
          )
        )}
      </AM>

      <DlgSensorPoolInfo open={showStats} onClose={onCloseStats} />

      <DlgSensorsExport
        open={exportDialog}
        onClose={onCloseExport}
        rows={tblSnapshot.rows}
        page={tblSnapshot.page}
      />

      <TblSensors
        data={data}
        onRowClick={id => gotoSensorPage(id)}
        onStateChange={state => setTblSnapshot(state)}
        onUpdateMaxResults={max =>
          setConfig(prev => ({ ...prev, maxResults: max }))
        }
      />
    </KPage>
  ) : (
    <Alert severity={'info'}>Admin Bereich</Alert>
  )
}
